import React from "react";
import { SeizureRestraintRecord } from "types";
import Accordion from "../../../components/accordion/Accordion"; // Adjust import path as needed

interface SeizureRestrainRecordsProps {
  records: SeizureRestraintRecord[];
  onEdit?: (record: SeizureRestraintRecord) => void;
  onDelete?: (recordId: string) => void;
  title?: string; // Optional custom title
  defaultOpen?: boolean; // Whether accordion is open by default
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: "bg-yellow-100 text-yellow-800",
    UNDER_SUPERVISION: "bg-blue-100 text-blue-800",
    RELEASED: "bg-green-100 text-green-800",
    CONFISCATED: "bg-purple-100 text-purple-800",
    TRANSFERRED: "bg-orange-100 text-orange-800",
    CANCELLED: "bg-red-100 text-red-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
};

const getSeizureTypeColor = (type: string) => {
  return type === "SEIZURE"
    ? "bg-red-100 text-red-800"
    : "bg-blue-100 text-blue-800";
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const SeizureRestrainRecords: React.FC<SeizureRestrainRecordsProps> = ({
  records,
  onEdit,
  onDelete,
  title = "Provisions",
  defaultOpen = false,
}) => {
  const recordCount = records?.length || 0;

  return (
    <Accordion
      mainTitle={title}
      title={`${recordCount} ${recordCount === 1 ? "Record" : "Records"}`}
      defaultOpen={defaultOpen}
    >
      {!records || records.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No seizure/restrain records found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supervising Authority
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supervising Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supervision Notes
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Release Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Release Reason
                </th>
                {(onEdit || onDelete) && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getSeizureTypeColor(
                        record.seizure_type,
                      )}`}
                    >
                      {record.seizure_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        record.status,
                      )}`}
                    >
                      {record.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {record.supervising_authority}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {record.supervising_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={record.supervision_notes}>
                      {record.supervision_notes || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {record.status === "RELEASED"
                      ? formatDate(record.release_date)
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                    {record.status === "RELEASED" ? (
                      <div className="truncate" title={record.release_reason}>
                        {record.release_reason || "-"}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  {(onEdit || onDelete) && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {onEdit && (
                          <button
                            onClick={() => onEdit(record)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Edit
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(record.id)}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Accordion>
  );
};
